import { supabase } from '../lib/supabase';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const getAuthToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token;
};

const getUserId = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id;
};

const randomString = (length = 12) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const generateApiKey = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16).toUpperCase();
  });
};

export const saveInstanceToSupabase = async ({ apikey, instanceName, name, server_id, user_id }) => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/instances`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ apikey, instanceName, name, server_id, user_id }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Erro ao salvar instância');
  }

  return await response.json();
};

// Ajusta Servers.current_instances para o valor real (count das Instances do servidor)
const syncServerCurrentInstances = async (server_id) => {
  try {
    const token = await getAuthToken();

    // O backend agora cuida dessa sincronização automaticamente
    // Mantém a função para compatibilidade mas não faz nada
    // TODO: Remover após confirmar que backend sincroniza automaticamente
  } catch (_) {
    // no-op
  }
};

export const createInstance = async (instanceName, userEmail) => {
  try {
    const token = await getAuthToken();
    const userId = await getUserId();

    if (!token || !userId) {
      throw new Error('Usuário não autenticado');
    }

    const apikey = generateApiKey();

    const body = {
      instanceName,
      name: instanceName,
      server_id: 1,
      apikey, // Envia apikey na primeira requisição
      user_id: userId, // Envia user_id na primeira requisição
      config: {
        webhooks: [
          {
            url: "https://automacao.wappfy.com.br/webhook/middleware/messages-upsert",
            events: ["message.any"]
          }
        ]
      }
    };

    const response = await fetch(`${API_BASE_URL}/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao criar instância');
    }

    const data = await response.json();

    let server_id = data?.server_id || 1;

    // ❌ REMOVIDO: Chamada duplicada ao saveInstanceToSupabase
    // O backend já deve salvar tudo na primeira requisição POST /sessions

    return {
      ...data,
      _wappfy: {
        apikey,
        instanceName: data.instanceName,
        name: instanceName,
        server_id,
        baseUrl: API_BASE_URL.replace(/\/api$/, '')
      }
    };
  } catch (error) {
    console.error('❌ [createInstance] Erro ao criar instância:', error);
    throw error;
  }
};

export const deleteInstance = async (instanceName) => {
  try {
    const token = await getAuthToken();
    const userId = await getUserId();

    if (!token || !userId) {
      throw new Error('Usuário não autenticado');
    }

    const response = await fetch(`${API_BASE_URL}/sessions/${instanceName}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao deletar instância');
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao deletar instância:', error);
    throw error;
  }
};

export const getInstances = async () => {
  try {
    const token = await getAuthToken();
    const userId = await getUserId();

    if (!token || !userId) {
      throw new Error('Usuário não autenticado');
    }

    const response = await fetch(`${API_BASE_URL}/instances/user/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erro ao buscar instâncias');
    }

    const result = await response.json();
    return result.instances || [];
  } catch (error) {
    console.error('Erro ao buscar instâncias:', error);
    throw error;
  }
};

export const getInstanceStatus = async (instanceName, server_id) => {
  try {
    const token = await getAuthToken();

    if (!token) {
      throw new Error('Usuário não autenticado');
    }

    const response = await fetch(`${API_BASE_URL}/sessions/${instanceName}/status?server_id=${server_id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao buscar status da instância');
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao buscar status da instância:', error);
    throw error;
  }
};

export const getInstanceQrCode = async (instanceName, server_id) => {
  try {
    const token = await getAuthToken();

    if (!token) {
      throw new Error('Usuário não autenticado');
    }

    if (!instanceName || !server_id) {
      console.error('Parâmetros recebidos:', { instanceName, server_id });
      throw new Error('instanceName e server_id são obrigatórios (body, params ou query)');
    }

    const response = await fetch(`${API_BASE_URL}/${instanceName}/auth/qr?server_id=${server_id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao buscar QR code da instância');
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao buscar QR code da instância:', error);
    throw error;
  }
};

export const restartInstance = async (instanceName, server_id) => {
  try {
    const token = await getAuthToken();

    if (!token) {
      throw new Error('Usuário não autenticado');
    }

    const response = await fetch(`${API_BASE_URL}/sessions/${instanceName}/restart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        instanceName,
        server_id
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao reiniciar instância');
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao reiniciar instância:', error);
    throw error;
  }
};

export const deleteInstanceFull = async (instance) => {
  try {
    const token = await getAuthToken();
    const userId = await getUserId();

    if (!token || !userId) {
      throw new Error('Usuário não autenticado');
    }

    // Extrai os dados necessários do objeto instance/connection
    const instanceName = instance.instanceName || instance.instance;
    const server_id = instance.server_id || instance.rawData?.server_id || 1;

    if (!instanceName) {
      throw new Error('Nome da instância não encontrado');
    }

    const response = await fetch(`${API_BASE_URL}/sessions/${instanceName}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        instanceName,
        server_id
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao deletar instância');
    }

    // Backend já deleta do Supabase automaticamente
    // Remove a chamada direta ao Supabase

    return { success: true };
  } catch (error) {
    console.error('Erro ao deletar instância completa:', error);
    throw error;
  }
};

export const updateInstancePhoneNumber = async (instanceId, phoneNumber) => {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/instances/${instanceId}/phone`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ phone_number: phoneNumber }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erro ao atualizar telefone');
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao atualizar número de telefone da instância:', error);
    throw error;
  }
};

export const updateInstanceName = async (instanceId, name) => {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/instances/${instanceId}/name`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Erro do backend:', errorData);
      throw new Error(errorData.message || 'Erro ao atualizar nome');
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('❌ Erro ao atualizar nome da instância:', error);
    throw error;
  }
};

export const updateInstanceWebhook = async ({ connection, webhookUrl, events }) => {
  try {
    const token = await getAuthToken();

    if (!token) {
      throw new Error('Usuário não autenticado');
    }

    const instanceName = connection?.instanceName || connection?.instance;
    const server_id = connection?.server_id || connection?.rawData?.server_id;

    if (!instanceName || !server_id) {
      throw new Error('instanceName e server_id são obrigatórios');
    }

    // Webhook padrão que sempre deve estar presente
    const DEFAULT_WEBHOOK = {
      url: "https://automacao.wappfy.com.br/webhook/middleware/messages-upsert",
      events: ["message.any"]
    };

    // Monta o array de webhooks sempre incluindo o padrão
    const webhooks = [DEFAULT_WEBHOOK];

    // Se houver webhook customizado, adiciona ao array
    if (webhookUrl) {
      webhooks.push({
        url: webhookUrl,
        events: Array.isArray(events) ? events : [],
      });
    }

    const config = {
      webhooks: webhooks
    };

    const response = await fetch(`${API_BASE_URL}/sessions/${instanceName}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        config,
        server_id
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao atualizar webhook da instância');
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao atualizar webhook da instância:', error);
    throw error;
  }
};